import {View} from "../constants";
import { Box } from "@mui/system";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { useParams } from "react-router";
import MUIDataTable from "mui-datatables";
import { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { IconTestPipe } from "@tabler/icons";
import EditIcon from "@mui/icons-material/Edit";
import AddPatientTests from "./AddPatientTests";
import EditPatientTests from "./EditPatientTests";
import { configState } from "../../../recoil/atoms";
import LinearProgress from "@mui/material/LinearProgress";
import ViewFile from "../../../utils/FormFields/ViewFile";
import { Button, Grid, TableCell, TableRow } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MainCard from "../../../styles/ui-component/cards/MainCard";
import ConfirmDialog from "../../../utils/FormFields/ConfirmDialog";
import useAuthorization from "../../Authentication/permissions/Authorization";
import { PatientTests as PatientTestsType, PatientTestsApi, AppointmentApi } from "../../../api/medical-operations";

export default function PatientTests() {
  const config = useRecoilValue(configState);
  let { patientID, appointmentID } = useParams();

  const [rowID, setRowID] = useState<string>("");
  const [isClosed, setIsClosed] = useState<string>();
  const [view, setView] = useState<View>(View.TABLE);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [editInfo, setEditInfo] = useState<PatientTestsType | any>();
  const [PatientTests, setPatientTest] = useState<PatientTestsType[]>([]);

  const { canAddPatientTests, canGetPatientTests, canEditPatientTests, canDeletePatientTests } = useAuthorization();

  const getAppointment = () => {
    const appointment = new AppointmentApi(config);
    appointment
      .getAppointmentById(appointmentID!)
      .then((res) => {
        setIsLoading(false);        
        setIsClosed(res.data.Status);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };

  const getPatientTests = () => {
    const PatientTests = new PatientTestsApi(config);
    PatientTests.getPatientTests(patientID, appointmentID)
      .then((res) => {
        setIsLoading(false);
        setPatientTest(res?.data);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err?.response?.data?.detail);
      });
  };

  useEffect(() => {
    getAppointment();
    getPatientTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view!]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setRowID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deletePatientTest(rowID);
    handleCloseDialog();
  };

  const deletePatientTest = (id: string) => {
    const PatientTests = new PatientTestsApi(config);
    PatientTests.deletePatientTests(id)
      .then((res) => {
        getPatientTests();
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const columns = [
    {
      name: "PatientTestID",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false,
      },
    },
    {
      name: "TestName",
      label: "Test Name ",
    },
    {
      name: "ResultOutcomes",
      label: "Result Outcomes ",
    },
    {
      name: "ReleaseDate",
      label: "Release Date ",
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => <>{value.slice(0, 10)}</>,
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        print: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
          <>
            {canEditPatientTests && (
              <Tooltip title="Edit">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setView(View.EDIT);
                    setEditInfo(tableMeta.tableData[tableMeta.rowIndex]);
                  }}
                >
                  <EditIcon color="secondary" />
                </Button>
              </Tooltip>
            )}
            {canDeletePatientTests && (
              <Tooltip title="Delete">
                <Button
                  variant="outlined"
                  color="error"
                  sx={{
                    ml: { xs: 0, xl: 2 },
                  }}
                  onClick={() => {
                    handleDelete(tableMeta.rowData[0]);
                  }}
                >
                  <DeleteOutlineIcon color="error" />
                </Button>
              </Tooltip>
            )}
          </>
        ),
      },
    },
  ];

  const expandedResultsColumns = [
    {
      name: "ResultsDocuments.URI",
      label: "Result",
      options: {
        customBodyRender: (val: any) => {
          return <ViewFile value={val} />;
        },
      },
    },
    {
      name: "ResultsDocuments.ImageCategory",
      label: "Category",
    },
    {
      name: "ResultsDocuments.UploadedByDoctor",
      label: "Uploaded By Doctor",
    },
    {
      name: "ResultsDocuments.Description",
      label: "Description",
    },
  ];
  return (
    <>
      {canGetPatientTests && (
        <MainCard>
          {!isLoading ? (
            <>
              <Button
                disabled={view === View.TABLE}
                variant="outlined"
                color="secondary"
                onClick={() => setView(View.TABLE)}
                sx={{ mb: 2 }}
              >
                View Tests
              </Button>

              {view === View.TABLE ? (
                <Grid>
                  <>
                    <MUIDataTable
                      data={PatientTests}
                      columns={columns}
                      options={{
                        filter: true,
                        viewColumns: false,
                        selectableRows: "none",
                        selectableRowsOnClick: false,
                        elevation: 0,
                        rowsPerPage: 10,
                        filterType: "dropdown",
                        enableNestedDataAccess: ".",
                        responsive: "standard",
                        expandableRows: true,
                        customToolbar: () => {
                          return appointmentID && canAddPatientTests && isClosed !== 'Closed' ? (
                            <Button
                              variant="contained"
                              color="secondary"
                              startIcon={<IconTestPipe />}
                              onClick={() => {
                                setView(View.ADD);
                              }}
                              sx={{
                                ml: "50px",
                              }}
                            >
                              Add Patient Test
                            </Button>
                          ) : null;
                        },
                        renderExpandableRow: (rowData, rowMeta) => {
                          const colSpan = rowData.length + 1;
                          return (
                            <>
                              <TableRow>
                                <TableCell colSpan={colSpan}>
                                  <MUIDataTable
                                    data={PatientTests[rowMeta.dataIndex].ResultsDocuments.map((item, index) => {
                                      return [
                                        item.URI,
                                        item?.ImageCategory,
                                        item?.UploadedByDoctor ? "Yes" : "No",
                                        item?.Description,
                                      ];
                                    })}
                                    columns={expandedResultsColumns}
                                    title="Result Documents"
                                    options={{
                                      viewColumns: false,
                                      selectableRows: "none",
                                      selectableRowsOnClick: false,
                                      elevation: 0,
                                      filter: false,
                                      pagination: false,
                                      download: false,
                                      print: false,
                                      search: false,
                                      rowsPerPage: 8,
                                      filterType: "dropdown",
                                      enableNestedDataAccess: ".",
                                      responsive: "standard",
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            </>
                          );
                        },
                      }}
                      title=""
                    />
                    <ConfirmDialog
                      name={"patient test"}
                      handleCloseDialog={handleCloseDialog}
                      handleConfirm={handleConfirm}
                      openDialog={openDialog}
                    />
                  </>
                </Grid>
              ) : view === View.ADD ? (
                <Box>
                  <AddPatientTests appointmentID={appointmentID} />
                </Box>
              ) : (
                <EditPatientTests initialPatientTestValues={editInfo} />
              )}
            </>
          ) : (
            <LinearProgress color="secondary" />
          )}
        </MainCard>
      )}
    </>
  );
}
